import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Youtube, Save, Users, Plus, Trash2, ChevronDown } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CATS = ["easy", "intermediate", "challenging"];
const CAT_COLOR = { easy: "#E0E0E0", intermediate: "#D4AF37", challenging: "#C41E24" };

function Row({ token, d, onSaved, onDeleted }) {
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [f, setF] = useState({ title: d.title, description: d.description, standard: d.standard, category: d.category, video_url: d.video_url });
  const [busy, setBusy] = useState(false);
  const dirty = f.title !== d.title || f.description !== d.description || f.standard !== d.standard || f.category !== d.category || f.video_url !== d.video_url;
  const save = async () => {
    setBusy(true);
    try { await axios.put(`${API}/coach/free-drills/${d.id}`, f, auth); toast.success("Drill saved."); onSaved(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Save failed."); } finally { setBusy(false); }
  };
  const inp = "w-full bg-[#0d0d0d] border border-white/10 rounded-md px-3 py-2 text-sm text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#C41E24]";
  return (
    <div className="bg-[#141414] border border-white/10 rounded-lg p-4 space-y-2.5" data-testid={`fd-row-${d.id}`}>
      <div className="flex gap-2">
        <input data-testid={`fd-title-${d.id}`} value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="Title" className={inp} />
        <select value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })}
          className="bg-[#0d0d0d] border border-white/10 rounded-md px-2 text-xs font-bold uppercase text-white" style={{ color: CAT_COLOR[f.category] }}>
          {CATS.map((c) => <option key={c} value={c} style={{ color: "#fff", background: "#111" }}>{c}</option>)}
        </select>
      </div>
      <textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} rows={2} placeholder="Description" className={inp} />
      <input value={f.standard} onChange={(e) => setF({ ...f, standard: e.target.value })} placeholder="Next O Standard cue" className={inp} />
      <div className="relative">
        <Youtube size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#C41E24]" />
        <input data-testid={`fd-video-${d.id}`} value={f.video_url} onChange={(e) => setF({ ...f, video_url: e.target.value })}
          placeholder="YouTube link (youtube.com/shorts/... or watch?v=...)" className={`${inp} pl-9`} />
      </div>
      <div className="flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-widest" style={{ color: d.youtube_id ? "#22C55E" : "#737373" }}>
          {d.youtube_id ? "● Video linked" : "○ No video yet"}
        </span>
        <div className="flex gap-2">
          <button data-testid={`fd-delete-${d.id}`} onClick={() => { if (window.confirm("Delete this drill?")) { axios.delete(`${API}/coach/free-drills/${d.id}`, auth).then(onDeleted); } }}
            className="text-white/40 hover:text-[#C41E24] p-2"><Trash2 size={15} /></button>
          <button data-testid={`fd-save-${d.id}`} onClick={save} disabled={!dirty || busy}
            className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider bg-[#C41E24] hover:bg-[#A0161D] text-white px-4 py-2 rounded-sm disabled:opacity-40">
            <Save size={13} /> Save
          </button>
        </div>
      </div>
    </div>
  );
}

export default function FreeDrillsManager({ token }) {
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [drills, setDrills] = useState([]);
  const [leads, setLeads] = useState(null);
  const [showLeads, setShowLeads] = useState(false);
  const load = () => axios.get(`${API}/coach/free-drills`, auth).then(({ data }) => setDrills(data.drills)).catch(() => {});
  const loadLeads = () => axios.get(`${API}/coach/newsletter-leads`, auth).then(({ data }) => setLeads(data)).catch(() => {});
  useEffect(() => { load(); loadLeads(); /* eslint-disable-next-line */ }, []);

  const addDrill = () => axios.post(`${API}/coach/free-drills`, { title: "New Drill", category: "easy", description: "", standard: "" }, auth)
    .then(load).then(() => toast.success("Drill added."));

  return (
    <div data-testid="free-drills-manager">
      <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
        <div className="flex items-center gap-2"><Youtube size={18} className="text-[#C41E24]" /><h2 className="font-headings text-2xl uppercase tracking-wide">Free Drill Sampler</h2></div>
        <div className="flex gap-2">
          <button data-testid="fd-view-leads" onClick={() => { setShowLeads((s) => !s); loadLeads(); }}
            className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider bg-white/5 border border-white/15 text-white/70 hover:text-white px-3 py-2 rounded-sm">
            <Users size={14} /> Leads {leads ? `· ${leads.count}` : ""}
          </button>
          <button data-testid="fd-add" onClick={addDrill} className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider bg-[#D4AF37] text-black px-3 py-2 rounded-sm"><Plus size={14} /> Add</button>
        </div>
      </div>
      <p className="text-sm text-white/45 mb-5 max-w-2xl">Paste your @NextOSoccer YouTube links, edit titles, descriptions and the Next O Standard cue. Public page: <span className="text-[#D4AF37]">/free-drills</span></p>

      {showLeads && (
        <div className="bg-[#141414] border border-white/10 rounded-lg p-4 mb-5 max-h-64 overflow-y-auto" data-testid="fd-leads-list">
          <div className="text-[11px] font-bold uppercase tracking-widest text-white/50 mb-2">Newsletter Leads ({leads?.count || 0})</div>
          {(leads?.leads || []).length === 0 ? <p className="text-sm text-white/40">No signups yet.</p> :
            (leads.leads).map((l, i) => (
              <div key={i} className="flex justify-between text-sm py-1 border-b border-white/5">
                <span className="text-white/80">{l.email}</span>
                <span className="text-white/40 text-xs">{(l.created_at || "").slice(0, 10)} · {l.source}</span>
              </div>
            ))}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {drills.map((d) => <Row key={d.id} token={token} d={d} onSaved={load} onDeleted={load} />)}
      </div>
    </div>
  );
}
